import type {
  SignInInput,
  SignInResponse,
  ForgotPasswordInput,
  ResetPasswordInput,
  ChangePasswordInput,
  StoredAuth,
  AuthMethod,
  FederatedMethod,
  MeResponse,
} from '../../domain/entities/auth.entity';
import type { AuthApiDatasource } from '../datasources/auth-api.datasource';
import {
  AuthStorageDatasource,
  reauthenticateAndChangePassword,
} from '@react-apps/auth';
import { firebaseAuth } from '@/lib/firebase';
import {
  signInWithEmailAndPassword,
  signInWithCustomToken as fbSignInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  linkWithPopup,
} from 'firebase/auth';

/**
 * O provedor do popup. Na Microsoft, `select_account` faz a pessoa escolher a
 * conta em vez de entrar calada com a que o navegador já tem logada — com conta
 * pessoal e de trabalho no mesmo navegador, a errada é o caso comum.
 */
function federatedProvider(method: FederatedMethod) {
  if (method === 'google.com') return new GoogleAuthProvider();
  const provider = new OAuthProvider('microsoft.com');
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

export class AuthRepositoryImpl {
  private readonly api: AuthApiDatasource;
  private readonly storage: AuthStorageDatasource<StoredAuth>;

  constructor(
    api: AuthApiDatasource,
    storage: AuthStorageDatasource<StoredAuth>,
  ) {
    this.api = api;
    this.storage = storage;
  }

  async signInWithToken(firebaseToken: string): Promise<SignInResponse> {
    const data = await this.api.signInWithFirebaseToken(firebaseToken);
    const accessToken =
      data.accessToken ?? data.tokens?.accessToken ?? firebaseToken;
    const accountId = data.account?.id ?? data.user?.id ?? '';
    const email = data.account?.email ?? data.user?.email ?? '';
    if (accountId) {
      this.storage.save({ accessToken, accountId, email });
    }
    return data;
  }

  async signIn(input: SignInInput): Promise<SignInResponse> {
    const credential = await signInWithEmailAndPassword(
      firebaseAuth,
      input.email,
      input.password,
    );
    const firebaseToken = await credential.user.getIdToken();
    const data = await this.api.signInWithFirebaseToken(firebaseToken);
    const accessToken =
      data.accessToken ?? data.tokens?.accessToken ?? firebaseToken;
    const accountId = data.account?.id ?? data.user?.id ?? '';
    const email = data.account?.email ?? data.user?.email ?? input.email;
    if (accountId) {
      this.storage.save({ accessToken, accountId, email });
    }
    return data;
  }

  /**
   * SSO handoff vindo do admin-dashboard: troca o custom token (emitido pelo
   * gateway para o mesmo uid) por uma sessão Firebase e conclui o login normal.
   */
  async signInWithCustomToken(customToken: string): Promise<SignInResponse> {
    const credential = await fbSignInWithCustomToken(firebaseAuth, customToken);
    const firebaseToken = await credential.user.getIdToken();
    return this.signInWithToken(firebaseToken);
  }

  async signInWithGoogle(): Promise<SignInResponse> {
    return this.signInWithFederated('google.com');
  }

  async signInWithMicrosoft(): Promise<SignInResponse> {
    return this.signInWithFederated('microsoft.com');
  }

  private async signInWithFederated(
    method: FederatedMethod,
  ): Promise<SignInResponse> {
    const credential = await signInWithPopup(
      firebaseAuth,
      federatedProvider(method),
    );
    const firebaseToken = await credential.user.getIdToken();
    return this.signInWithToken(firebaseToken);
  }

  async forgotPassword(input: ForgotPasswordInput): Promise<void> {
    return this.api.forgotPassword(input);
  }

  async resetPassword(input: ResetPasswordInput): Promise<void> {
    return this.api.resetPassword(input);
  }

  /**
   * Reautentica com a senha atual (prova de posse + renova o `auth_time`, exigido
   * pelo RecentAuthGuard do back) e troca a senha. **Não** faz o re-login: o back
   * revoga TODAS as sessões (inclusive esta), então quem chama refaz o login com a
   * senha nova (`signIn`) — separado para distinguir "troca falhou" de "trocou mas
   * o re-login caiu". Devolve o e-mail (lido antes da revogação) para esse re-login.
   */
  /**
   * Troca obrigatória (senha temporária). A sequência (reautenticar → refresh
   * forçado do token → trocar) é compartilhada; o que é deste app é a chamada
   * HTTP — o pacote não conhece `apiClient` nem a rota.
   */
  reauthenticateAndChangePassword(input: ChangePasswordInput): Promise<string> {
    return reauthenticateAndChangePassword({
      auth: firebaseAuth,
      input,
      changePassword: (payload) =>
        this.api.changePassword({ newPassword: payload.newPassword }),
    });
  }

  async verifyEmail(token: string): Promise<void> {
    return this.api.verifyEmail(token);
  }

  async sendVerification(email: string): Promise<void> {
    return this.api.sendVerification(email);
  }

  /** Custom token para entrar em outra plataforma como o próprio usuário. */
  async createHandoffToken(): Promise<string> {
    const { customToken } = await this.api.createHandoffToken();
    return customToken;
  }

  async getMe(): Promise<MeResponse> {
    return this.api.getMe();
  }

  // ── Formas de login (google-sign-in §4.2) ───────────────────────────────────

  /**
   * Vincula um provedor à conta JÁ autenticada — `linkWithPopup` (não
   * `signInWithPopup`, que trocaria de sessão). O vínculo acontece no provedor;
   * o backend só espelha, via a rota de sync que já existia — não há rota de
   * "linkar". O refresh do ID token é para o `x-auth-providers` que o gateway
   * injeta não seguir descrevendo a sessão de antes do vínculo.
   */
  async linkFederated(method: FederatedMethod): Promise<MeResponse> {
    const user = firebaseAuth.currentUser;
    if (!user) {
      throw new Error('Sessão expirada. Entre novamente.');
    }
    await linkWithPopup(user, federatedProvider(method));
    await user.getIdToken(true);
    return this.api.syncIdentities();
  }

  /**
   * Reautentica (prova de posse + renova o `auth_time` exigido pelo
   * RecentAuthGuard) e desvincula a forma de login. Reautentica com a senha
   * quando a conta tem uma; senão, pelo popup do federado vinculado — Google
   * antes de Microsoft, quando há os dois. Qualquer método ainda
   * vinculado serve para provar posse — e desvincular só é permitido quando
   * resta outro, então sempre há um. O backend recusa remover o último
   * (CANNOT_UNLINK_LAST_IDENTITY).
   */
  async reauthenticateAndUnlinkIdentity(
    method: AuthMethod,
    currentPassword?: string,
  ): Promise<MeResponse> {
    const user = firebaseAuth.currentUser;
    if (!user) {
      throw new Error('Sessão expirada. Entre novamente.');
    }

    if (currentPassword) {
      if (!user.email) {
        throw new Error('Sessão expirada. Entre novamente.');
      }
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );
      await reauthenticateWithCredential(user, credential);
    } else {
      const linked = user.providerData.map((p) => p.providerId);
      const method: FederatedMethod = linked.includes('google.com')
        ? 'google.com'
        : 'microsoft.com';
      await reauthenticateWithPopup(user, federatedProvider(method));
    }
    // Sem o refresh forçado, o interceptor reenviaria o token em cache (com o
    // `auth_time` antigo) e o back recusaria com 403.
    await user.getIdToken(true);

    return this.api.unlinkIdentity(method);
  }

  /**
   * Ato 1 da troca de e-mail: reautentica com a senha atual (prova de posse +
   * renova o `auth_time` exigido pelo RecentAuthGuard) e chama o `start`. A senha
   * morre aqui, não atravessa o HTTP.
   */
  async startEmailChange(currentPassword: string): Promise<void> {
    const user = firebaseAuth.currentUser;
    if (!user?.email) {
      throw new Error('Sessão expirada. Entre novamente.');
    }
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword,
    );
    await reauthenticateWithCredential(user, credential);
    await user.getIdToken(true);
    await this.api.startEmailChange();
  }

  async submitEmailChange(input: {
    token: string;
    newEmail: string;
  }): Promise<void> {
    return this.api.submitEmailChange(input);
  }

  async confirmEmailChange(token: string): Promise<void> {
    return this.api.confirmEmailChange(token);
  }

  async cancelEmailChange(): Promise<void> {
    return this.api.cancelEmailChange();
  }

  getStoredAuth(): StoredAuth | null {
    return this.storage.get();
  }

  saveAuth(auth: StoredAuth): void {
    this.storage.save(auth);
  }

  /**
   * Derruba a sessão: apaga o snapshot local e desloga do Firebase.
   *
   * Devolve promise porque **esperar importa** para quem navega ao `/sign-in`
   * em seguida: essa tela entra sozinha ao encontrar sessão Firebase viva
   * (`onAuthStateChanged`), e um `signOut` ainda em vôo faria essa entrada
   * automática disparar com a sessão que acabamos de invalidar. Quem só quer
   * limpar e seguir (logout do menu, saída do muro) chama com `void`.
   */
  async clearAuth(): Promise<void> {
    this.storage.clear();
    try {
      await firebaseAuth.signOut();
    } catch {
      // Nunca rejeita: quem chama está no meio de tratar OUTRO erro e depende
      // de seguir para exibir a mensagem. Uma falha aqui (persistência do
      // Firebase) não pode virar rejeição que engole o recado da tela — o
      // snapshot local já saiu, que é a parte sob nosso controle.
    }
  }
}
